import { CtControlTypes, CtModelOperator, CtSelectControlValue } from "@ctsolution/ct-framework";
import { CustomCompanySetupEnum } from "../../components/company/company-edit/company-edit.component";

/**
 * Configurazione di un singolo campo LeadInformation da aggiungere dinamicamente
 * al form del lead.
 *
 * - `type`           : codice numerico dell'enum ContactInformationTypeVO (es. 101)
 * - `controlType`    : tipo di controllo Angular Material (default: CtControlTypes.TEXT)
 * - `flexPercentage` : larghezza percentuale nella griglia del form (default: 50)
 */
export interface LeadInformationFieldConfig {
  type: number;
  controlType?: CtControlTypes;
  flexPercentage?: number;
}

export class CTConfigurationModel {

  visible: boolean = true;
  customCompanySetup: CustomCompanySetupEnum = CustomCompanySetupEnum.BASE;
  customContactSetup: CustomCompanySetupEnum = CustomCompanySetupEnum.BASE;
  customCompanyTemplate = 'company-information-model.json';
  customContactTemplate = 'contact-information-model.json';
  label?: string;
  type?: number;
  cols: Array<string> = [];
  visibleWorkOrder: boolean = true;
  doubleClickOpenDetail: boolean = false;
  expanded: boolean = false;
  formatPrice: number = 2;
  formatNumber: number = 2;
  multiCheckBoxTable: boolean = false;
  bypassLeadStatus: boolean = false;
  useDefaultVAT: boolean = false;
  quotationVisible: boolean = false;

  /**
   * Quando `true`, il default del lookup "Tipologia cliente" (CompanyType) viene preso
   * dall'elemento del datasource marcato come `Default = true` a DB (vedi tabella CompanyType,
   * colonna `Default`).
   *
   * Default `false` per backward-compatibility: gli altri client continuano a usare il
   * default basato sul macro-tipo (Private/Business) impostato via `setType`.
   *
   * Va abilitato esplicitamente per-client tramite
   * `getComponentOverride("company", "companyType", configurationModel)`.
   */
  useDefaultCompanyType: boolean = false;

  visibleHintRowWorkOrder: boolean = true;
  cloneRowEnabled: boolean = true;
  
  // Abilita la modalità ConfigurationCode per le Quotation (passa ConfigurationCode nel body invece di filtri client-side)
  enableConfigurationCodeMode: boolean = false;
  exportExcelEnabled: boolean = false;

  /**
   * Abilita la card "Reportistica" in dashboard.
   * Alla pressione chiama GET BI/GetAccess e apre il link SSO restituito in una nuova scheda.
   * Default `false` per backward-compatibility: va abilitata esplicitamente per-client
   * tramite `getComponentOverride("dashboard", "biConfiguration", ...)` nel portale.
   */
  biConfiguration: boolean = false;

  // Controlla se abilitare la navigazione onCreate nel work-order-list
  allowOnCreate: boolean = true;

  // Controlla se mostrare il bottone Detail nella handling-list
  // E se mostrare la textarea ItemNotes nello step1 del wizard (default true = backward compatible)
  handlingDetailEnabled: boolean = true;

  /**
   * Abilita la sezione LeadInformation nel form del lead.
   * Default `false` per backward-compatibility: la funzionalità si attiva
   * esplicitamente per-client tramite `getComponentOverride`.
   */
  leadInformationEnabled: boolean = false;

  /**
   * Elenco dei campi LeadInformation da aggiungere dinamicamente al form del lead.
   * Ogni entry specifica il codice del tipo, il tipo di controllo e la larghezza.
   * Valori omessi vengono completati con i default (TEXT, 50%).
   * Esempio:
   *   [
   *     { type: 101, controlType: CtControlTypes.TEXT, flexPercentage: 50 },
   *     { type: 102, controlType: CtControlTypes.TEXTAREA, flexPercentage: 100 }
   *   ]
   */
  leadInformationFields: LeadInformationFieldConfig[] = [];

  // Colonne da mostrare nella dialog di dettaglio articoli dell'ordine.
  // Valori ammessi: 'code' | 'name' | 'description' | 'price' | 'qty' | 'additionalCode' | 'price1000'
  handlingDetailVisibleColumns: string[] = ['code', 'name', 'description', 'additionalCode', 'price', 'price1000', 'qty'];

  showTotalRow: boolean = false;
  legendsItem: Array<any> = [
    { color: '#A82A26', label: 'Presentation Call', description: 'Appuntamento di presentazione ufficiale con cliente o prospect' },
    { color: '#2196F3', label: 'Appuntamento', description: 'Appuntamento con potenziale cliente' },
    { color: '#E1C11F', label: 'Mail', description: 'Mail in attesa di essere inviata' },
    { color: '#06820D', label: 'Inizio lavori', description: 'Avvio operativo' },
    { color: '#780E82', label: 'Offerta - Da inviare', description: 'Offerta pronta, in attesa di essere inviata' },
    { color: '#FF9800', label: 'Call', description: 'Telefonata per seguire una trattativa già avviata' },
    { color: '#FF148D', label: 'Lead - Attività proattiva', description: 'Iniziativa autonoma verso nuovi lead' },
    { color: '#12CED5', label: 'Offerta - In trattativa', description: 'Offerta inviata, in attesa di risposta o trattativa attiva' },
    { color: '#884916', label: 'Verifica stato', description: 'Controllo avanzamento lavori' },
    { color: '#44D130', label: 'Proattiva', description: 'Promemoria di proattività' },
    { color: '#008080', label: 'Follow up', description: 'Controllo stato avanzamento' },
    { color: '#000000', label: 'Kick Off', description: 'Kick off con cliente o interno' }
  ];

  visibleFields: string[] = [
    'DisplayName',
    'ReferenceName',
    'FiscalCode',
    'VAT',
    'Places'
  ];

  valueOptions: any | null = null;

  /**
   * Override delle operazioni abilitate sulla datatable del modulo payments.
   *
   * - `null` (default): mantiene il comportamento legacy hard-coded in `payments-list`
   *   (Update, Detail, Export). Nessun impatto sui client esistenti.
   * - valorizzato: sostituisce completamente l'elenco delle operazioni con quanto
   *   fornito (es. `[Update, Detail, Export, Create]` per abilitare la creazione).
   *
   * Va impostato esplicitamente per-client tramite
   * `getComponentOverride("payments", "paymentConfiguration", ...)`.
   */
  paymentsOperations: CtModelOperator[] | null = null;

  /**
   * Override dei campi mostrati nel form di edit/create del modulo payments.
   *
   * - `null` (default): il form mostra tutti i controlli storici
   *   (Description, Amount, PaymentDate, Status, Company, UserInfo, Contact, Notes).
   *   Nessun impatto sui client esistenti.
   * - valorizzato: filtra (e riordina) i controlli mantenendo solo quelli i cui
   *   `name` sono elencati nell'array, nell'ordine fornito. I controlli non
   *   inclusi vengono rimossi dal form.
   *
   * I nomi accettati corrispondono ai `name` dei `CtControlConfiguration` definiti
   * in `PaymentsEditComponent`: "Description", "Amount", "PaymentDate",
   * "ExpireDate", "Status", "Company", "WorkOrder", "UserInfo", "Contact", "Notes".
   *
   * Va impostato esplicitamente per-client tramite
   * `getComponentOverride("payments", "paymentConfiguration", ...)`.
   */
  paymentsFormControls: string[] | null = null;

  /**
   * Override dei campi che devono essere `required` nel form di edit/create del modulo payments.
   *
   * - `null` (default): nessun campo viene reso required oltre a quanto già dichiarato
   *   nei `CtControlConfiguration` originali. Nessun impatto sui client esistenti.
   * - valorizzato: i control il cui `name` è elencato vengono forzati a `required = true`
   *   tramite `Validators.required` runtime, applicato dopo l'eventuale filtro di
   *   `paymentsFormControls`.
   *
   * I nomi accettati sono gli stessi di `paymentsFormControls`: "Description",
   * "Amount", "PaymentDate", "ExpireDate", "Status", "Company", "WorkOrder",
   * "UserInfo", "Contact", "Notes".
   *
   * Va impostato esplicitamente per-client tramite
   * `getComponentOverride("payments", "paymentConfiguration", ...)`.
   */
  paymentsRequiredFormControls: string[] | null = null;

  /**
   * Override del filtro `IsSelf` sul lookup Company nel form payments.
   *
   * - `null` (default): applica `IsSelf = true` (comportamento storico: il lookup
   *   mostra solo le aziende marcate come "azienda propria" del tenant). Nessun
   *   impatto sui client esistenti.
   * - `true`: applica `IsSelf = true` (equivalente al default, esplicito).
   * - `false`: NON applica il filtro su `IsSelf` (il lookup mostra tutte le aziende:
   *   utile quando il pagamento deve essere associato a un cliente).
   *
   * Va impostato esplicitamente per-client tramite
   * `getComponentOverride("payments", "paymentConfiguration", ...)`.
   */
  paymentCompanyLookupSelfOnly: boolean | null = null;

  /**
   * Visibilità della sezione "Dati fatturazione" nel form del Task edit.
   *
   * - `false` (default): la sezione (titolo + <ct-form>) NON viene renderizzata.
   *   `invoiceConfiguration` resta `null` e i campi InvoiceNumber/InvoiceDate/
   *   InvoiceState/InvoiceTotal/InvoiceVAT/InvoiceComments non compaiono.
   *   Il submit ignora la sezione e non manda al BE i relativi dati (i valori
   *   a DB restano invariati: fix solo FE).
   * - `true`: sezione visibile (comportamento storico). Va abilitato esplicitamente
   *   per-client tramite `getComponentOverride("task", "taskConfiguration", ...)`.
   */
  taskInvoiceSectionVisible: boolean = false;

  constructor() { }

  public static create = (): CTConfigurationModel => new CTConfigurationModel();

  setVisible(value: boolean): CTConfigurationModel {
    this.visible = value;
    return this;
  }

  setLabel(value: string): CTConfigurationModel {

    this.label = value;
    return this;

  }

  setType(value: number): CTConfigurationModel {
    this.type = value;
    return this;
  }

  setCols(value: Array<string>): CTConfigurationModel {
    this.cols = value;
    return this;
  }

  setCustomSetup(value: CustomCompanySetupEnum): CTConfigurationModel {
    this.customCompanySetup = value;
    return this;
  }
  setDoubleClickOpenDetail(value: boolean): CTConfigurationModel {
    this.doubleClickOpenDetail = value;
    return this;
  }

  setExpanded(value: boolean): CTConfigurationModel {

    this.expanded = value;
    return this;

  }

  setLegendsItem(value: Array<any>): CTConfigurationModel {

    this.legendsItem = value;
    return this;

  }

  setBiConfiguration(value: boolean): CTConfigurationModel {

    this.biConfiguration = value;
    return this;

  }

  setUseDefaultCompanyType(value: boolean): CTConfigurationModel {

    this.useDefaultCompanyType = value;
    return this;

  }

  setPaymentsOperations(value: CtModelOperator[] | null): CTConfigurationModel {

    this.paymentsOperations = value;
    return this;

  }

  setPaymentsFormControls(value: string[] | null): CTConfigurationModel {

    this.paymentsFormControls = value;
    return this;

  }

  setPaymentsRequiredFormControls(value: string[] | null): CTConfigurationModel {

    this.paymentsRequiredFormControls = value;
    return this;

  }

  setPaymentCompanyLookupSelfOnly(value: boolean | null): CTConfigurationModel {

    this.paymentCompanyLookupSelfOnly = value;
    return this;

  }

  setTaskInvoiceSectionVisible(value: boolean): CTConfigurationModel {

    this.taskInvoiceSectionVisible = value;
    return this;

  }

}
